import { FC, useState } from 'react'
import {
    FirmViewSectionContainer,
    FlexDiv,
    OrangeButton,
    OrangeButtonSimpleText,
    OrangeButtonOutlined
} from '../index'
import { MdDeleteOutline } from 'react-icons/md'
import { FirmDetailsAddressData, FirmDetailsUpdateAddressInputs, FirmDetailsUpdateContactsInputs } from 'src/dtos/portal'
import { useToast } from 'src/hooks/useToast'
import FirmViewEditAddress from './FirmViewEditAddress'
import StandardInput from 'src/components/basic/StandardInput'
import StandardFormatInput from 'src/components/basic/StandardFormatInput'

interface FirmViewAddressesProps {
    addresses: {
        mailingAddress: FirmDetailsAddressData | null;
        billingAddress: FirmDetailsAddressData | null;
    }
    firmId: number;
    saveAddress: (addressInputs: FirmDetailsUpdateAddressInputs) => Promise<void>
    removeBillingAddress: () => void;
    saveContact: (contactInputs: FirmDetailsUpdateContactsInputs) => Promise<void>
}

const FirmViewAddresses: FC<FirmViewAddressesProps> = ({
    addresses,
    firmId,
    saveAddress,
    removeBillingAddress,
    saveContact
}) => {
    const { callToast } = useToast()

    const emptyAddressInputs: FirmDetailsUpdateAddressInputs = {
        firmId,
        streetNumber: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        postalCode: '',
        suiteNumber: ''
    } as FirmDetailsUpdateAddressInputs
    
    const { mailingAddress, billingAddress } = addresses
    const [editAddressInputs, setEditAddressInputs] = 
        useState<FirmDetailsUpdateAddressInputs>(emptyAddressInputs)
    const [editContactInputs, setEditContactInputs] = useState<FirmDetailsUpdateContactsInputs>({
        email: addresses?.mailingAddress?.email ?? '',
        phoneNumber: addresses?.mailingAddress?.phone ?? '',
        fax: addresses?.mailingAddress?.fax ?? ''
    })
    
    const [isSaving, setIsSaving] = useState(false)
    const [mailingEditMode, setMailingEditMode] = useState(false)
    const [billingEditMode, setBillingEditMode] = useState(false)
    const [contactEditMode, setContactEditMode] = useState(false)

    const handleCancelEditMode = (type: 'mailing' | 'billing') => {
        setEditAddressInputs(emptyAddressInputs)
        if(type === 'mailing') setMailingEditMode(false)
        else setBillingEditMode(false)
    }

    const handleStartEditMode = (type: 'mailing' | 'billing') => {
        if(type === 'mailing') {
            setEditAddressInputs(prev => ({
                ...prev,
                addressType: 0,
                streetNumber: mailingAddress?.address?.streetNumber ?? '',
                line1: mailingAddress?.address?.addressLine1 ?? '',
                line2: mailingAddress?.address?.addressLine2 ?? '',
                city: mailingAddress?.address?.city ?? '',
                postalCode: mailingAddress?.address?.postalCode ?? '',
                suiteNumber: mailingAddress?.address?.suiteNumber ?? '',
                state : mailingAddress?.address?.stateCode ?? ''
            }))
            setMailingEditMode(true)
        } else {
            setEditAddressInputs(prev => ({
                ...prev,
                addressType: 1,
                streetNumber: billingAddress?.address?.streetNumber ?? '',
                line1: billingAddress?.address?.addressLine1 ?? '',
                line2: billingAddress?.address?.addressLine2 ?? '',
                city: billingAddress?.address?.city ?? '',
                postalCode: billingAddress?.address?.postalCode ?? '',
                suiteNumber: billingAddress?.address?.suiteNumber ?? '',
                state: billingAddress?.address?.stateCode ?? '',
            }))
            setBillingEditMode(true)
        }
    }

    const handleSaveAddress = async () => {
        try {
            await saveAddress(editAddressInputs)
            setEditAddressInputs(emptyAddressInputs)
            setMailingEditMode(false)
            setBillingEditMode(false)
        } catch(error: any) {
            const errorMessage = error.message || 'An error occurred while saving the address.'
            callToast({
                header: 'Unexpected Error',
                footer: errorMessage,
                type: 'error'
            })
        }
    }

    const handleRemoveBillingAddress = async () => {
        await removeBillingAddress()
    }

    const handleSaveContact = async () => {
        setIsSaving(true)
        await saveContact(editContactInputs)
        setMailingEditMode(false)
        setBillingEditMode(false)
        setContactEditMode(false)
        setIsSaving(false)
    }
    
    return (
        <FirmViewSectionContainer>
            <h1>Firm Addresses & Contact Information</h1>
            <FlexDiv dir='column' margin='32px 0 0'>
                <h4>FIRM CONTACT INFORMATION</h4>
                <hr style={{ marginTop: '16px' }} />
                <FlexDiv
                    justify='space-between'
                    align='flex-start'
                    margin="24px 0 0"
                    className='contact-item'
                >
                    {contactEditMode === true ? (
                        <FlexDiv gap="144px">
                            <FlexDiv dir="column" gap="24px">
                                <StandardInput
                                    label="Email"
                                    value={editContactInputs.email}
                                    onChange={e => setEditContactInputs(prev => ({ ...prev, email: e.target.value }))}
                                />
                                <StandardFormatInput
                                    label="Phone"
                                    maskStyle='phone'
                                    value={editContactInputs.phoneNumber}
                                    onValueChange={({ formattedValue }) => setEditContactInputs(prev => ({ ...prev, phoneNumber: formattedValue }))}
                                />
                                <StandardFormatInput
                                    label="Fax"
                                    maskStyle='phone'
                                    value={editContactInputs.fax}
                                    onValueChange={({ formattedValue }) => setEditContactInputs(prev => ({ ...prev, fax: formattedValue }))}
                                />
                            </FlexDiv>
                            <FlexDiv maxWidth='fit-content' gap="24px" align='center'>
                                <OrangeButtonSimpleText onClick={() => setContactEditMode(false)} disabled={isSaving}>
                                    Cancel
                                </OrangeButtonSimpleText>
                                <OrangeButton
                                    disabled={isSaving ? true : (mailingEditMode || billingEditMode)}
                                    onClick={handleSaveContact}
                                >
                                    Save
                                </OrangeButton>
                            </FlexDiv>
                        </FlexDiv>
                    ) : (
                        <>
                            <FlexDiv dir='column'>
                                <article>
                                    <strong>Email</strong>
                                    <span>{mailingAddress?.email ?? 'N/A'}</span>
                                </article>
                                <article>
                                    <strong>Phone</strong>
                                    <span>{mailingAddress?.phone ?? 'N/A'}</span>
                                </article>
                                <article>
                                    <strong>Fax</strong>
                                    <span>{mailingAddress?.fax ?? 'N/A'}</span>
                                </article>
                            </FlexDiv>
                            <FlexDiv justify='flex-end'>
                                <OrangeButton
                                    onClick={() => setContactEditMode(true)}
                                    disabled={billingEditMode || mailingEditMode}
                                >
                                    Edit
                                </OrangeButton>
                            </FlexDiv>
                        </>
                    )}
                </FlexDiv>
            </FlexDiv>
            <FlexDiv dir='column' margin='32px 0 0'>
                <h4>MAILING ADDRESS</h4>
                <hr style={{ marginTop: '16px' }} />
                
                {mailingEditMode ? (
                    <FirmViewEditAddress 
                        editAddressInputs={editAddressInputs}
                        setEditAddressInputs={setEditAddressInputs}
                        handleCancel={() => handleCancelEditMode('mailing')}
                        handleSave={handleSaveAddress}
                    />
                ) : (
                    <FlexDiv
                        justify='space-between'
                        align='flex-start'
                        margin="24px 0 0"
                        className='contact-item'
                    >
                        <FlexDiv>
                            <article>
                                <FlexDiv dir="column">
                                    <strong>Address</strong>                                    
                                    <span>{mailingAddress?.address?.streetNumber} {mailingAddress?.address?.addressLine1 ?? ''}</span>
                                    <span>{mailingAddress?.address?.addressLine2 ?? ''}</span>
                                    <span>{mailingAddress?.address?.suiteNumber ?? ''}</span>
                                    <span>{mailingAddress?.address?.city ?? ''}, {mailingAddress?.address?.stateCode ?? ''} {mailingAddress?.address?.postalCode ?? ''}</span>
                                </FlexDiv>
                            </article>
                        </FlexDiv>
                        <FlexDiv justify='flex-end'>
                            <OrangeButton
                                onClick={() => handleStartEditMode('mailing')}
                                disabled={billingEditMode || contactEditMode}
                            >
                                Edit
                            </OrangeButton>
                        </FlexDiv>
                    </FlexDiv>
                )}
            </FlexDiv>
            {!!billingAddress && !!billingAddress?.address && !billingEditMode ? (
                <FlexDiv dir='column' margin='64px 0 0'>
                    <h4>BILLING ADDRESS</h4>
                    <hr style={{ marginTop: '16px' }} />
                    <FlexDiv
                        justify='space-between'
                        align='flex-start'
                        margin="24px 0 0"
                    >
                        <FlexDiv>
                            <article>
                                <FlexDiv dir="column">
                                    <strong>Address</strong>
                                    <span>{billingAddress?.address?.streetNumber} {billingAddress?.address?.addressLine1 ?? ''}</span>
                                    <span>{billingAddress?.address?.addressLine2 ?? ''}</span>
                                    <span>{billingAddress?.address?.suiteNumber ?? ''}</span>
                                    <span>{billingAddress?.address?.city ?? ''}, {billingAddress?.address?.stateCode ?? ''} {billingAddress?.address?.postalCode ?? ''}</span>
                                </FlexDiv>
                            </article>
                        </FlexDiv>
                        <FlexDiv justify='flex-end' align='center' gap="28px">
                            <OrangeButtonSimpleText onClick={handleRemoveBillingAddress} disabled={mailingEditMode || contactEditMode}>
                                <MdDeleteOutline />
                                Remove
                            </OrangeButtonSimpleText>
                            <OrangeButton
                                onClick={() => handleStartEditMode('billing')}
                                disabled={mailingEditMode || contactEditMode}
                            >
                                Edit
                            </OrangeButton>
                        </FlexDiv>
                    </FlexDiv>
                </FlexDiv>
            ) : null}
            {billingEditMode ? (
                <FlexDiv dir='column' margin='64px 0 0'>
                    <h4>BILLING ADDRESS</h4>
                    <hr style={{ marginTop: '16px' }} />
                    <FirmViewEditAddress 
                        editAddressInputs={editAddressInputs}
                        setEditAddressInputs={setEditAddressInputs}
                        handleCancel={() => handleCancelEditMode('billing')}
                        handleSave={handleSaveAddress}
                    />
                </FlexDiv>
            ) : null}
            {(!billingAddress && !mailingEditMode && !billingEditMode && !contactEditMode) ? (
                <FlexDiv margin="64px 0 0 0">
                    <OrangeButtonOutlined
                        style={{ padding: '8px 48px' }}
                        onClick={() => handleStartEditMode('billing')}
                    >
                        + Add a Billing Address
                    </OrangeButtonOutlined>
                </FlexDiv>
            ) : null}
        </FirmViewSectionContainer>
    )
}

export default FirmViewAddresses